import { useCallback, useContext, useEffect, useRef, useState } from "react";
import SocketContext from "../../../../Contexts/SocketContext";
import { getUsageLimit } from "../../../../Services/api";
import { analyzeWithAI } from "../AIAnalysis/Helpers/analyzeWithAI";
import { fetchReport } from "../AIAnalysis/Helpers/fetchReport";
import { hasAllowedModels } from "../Helpers/analysisAvailability";
import { isAiServiceFailure } from "../Helpers/aiFailureState";
import {
  createCancelledProgress,
  createCompletedProgress,
  createDataReadyProgress,
  createErrorProgress,
  createInitialProgress,
  getLocalizedText,
  isCancelledError,
} from "./executionProgress";

const COMPLETED_PROGRESS_HIDE_MS = 3000;

export const useReportExecution = ({
  filters,
  getAdvancedReportInfo,
  getPeriodLabel,
  selectedLevel,
  selectedReport,
  texts,
  toast,
  t,
  user,
}) => {
  const Socket = useContext(SocketContext);
  const language = texts?.en === "Arabic" ? "ar" : "en";
  const getText = useCallback(
    (key, ar, en) => getLocalizedText(t, language, key, ar, en),
    [language, t]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportApiResponse, setReportApiResponse] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [usageLimitReached, setUsageLimitReached] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const abortControllerRef = useRef(null);
  const cancelRunRef = useRef(null);
  const requestIdRef = useRef(0);
  const progressDismissTimerRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    getUsageLimit(user.CustomerId).then((response) => {
      const { MaxLimit, Used } = response?.data?.data || {};
      setUsageLimitReached(MaxLimit <= Used);
    });
  }, [user]);

  useEffect(() => {
    const handleUsageLimit = ({ command, data }) => {
      if (command !== "Increment") return;
      const { MaxLimit, Used } = data || {};
      setUsageLimitReached(MaxLimit <= Used);
    };

    Socket.on("UsageLimit", handleUsageLimit);
    return () => Socket.off("UsageLimit", handleUsageLimit);
  }, [Socket]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      cancelRunRef.current?.();
      window.clearTimeout(progressDismissTimerRef.current);
    },
    []
  );

  const resetResults = useCallback(() => {
    setReportData(null);
    setReportApiResponse(null);
    setAiAnalysis(null);
  }, []);

  const updateAnalysisProgress = useCallback((progress) => {
    setAnalysisProgress((current) => ({
      active: true,
      progress: 0,
      hasStartedRun: false,
      status: "running",
      ...current,
      ...progress,
    }));
  }, []);

  const cancelAnalysis = useCallback(
    async ({ silent = false } = {}) => {
      const controller = abortControllerRef.current;
      const cancelRun = cancelRunRef.current;
      const hasActiveWork = isAnalyzing || isLoading || controller || cancelRun;

      if (!hasActiveWork) return false;

      requestIdRef.current += 1;
      abortControllerRef.current = null;
      cancelRunRef.current = null;
      controller?.abort();
      window.clearTimeout(progressDismissTimerRef.current);
      setIsLoading(false);
      setIsAnalyzing(false);
      setAnalysisProgress(createCancelledProgress(getText));

      if (typeof cancelRun === "function") {
        await cancelRun();
      }

      if (!silent) {
        toast({
          title: getLocalizedText(t, language, "reports.analysisCancelled", "تم إلغاء التحليل", "Analysis cancelled"),
          description: getLocalizedText(
            t,
            language,
            "reports.analysisCancelledDesc",
            "تم إيقاف التحليل قبل اكتماله.",
            "The analysis was stopped before completion."
          ),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }

      return true;
    },
    [getText, isAnalyzing, isLoading, language, t, toast]
  );

  const runReport = useCallback(
    async ({ reportRef, setCopied, setError, availableReports }) => {
      const requestId = requestIdRef.current + 1;
      const abortController = new AbortController();

      try {
        requestIdRef.current = requestId;
        abortControllerRef.current = abortController;
        cancelRunRef.current = null;
        setIsLoading(true);
        setIsAnalyzing(true);
        setAnalysisProgress(createInitialProgress(getText));
        setError(null);
        setCopied(false);
        resetResults();

        const selectedAiReport = availableReports.aiAnalysis.find(
          (report) => report.ReportKey === selectedReport
        );

        if (!hasAllowedModels(selectedAiReport?.AllowedModels)) {
          throw new Error(
            getLocalizedText(
              t,
              language,
              "reports.analysisUnavailableContactSupport",
              "هذا التقرير غير متاح للتحليل الذكي حاليًا. يرجى التواصل مع الدعم.",
              "AI analysis is currently unavailable for this report. Please contact support."
            )
          );
        }

        const reportResponse = await fetchReport({
          filters,
          getPeriodLabel,
          selectedLevel,
          selectedReport,
          signal: abortController.signal,
          texts,
          toast,
          t,
        });

        if (requestIdRef.current !== requestId) return null;

        updateAnalysisProgress(createDataReadyProgress(getText));

        const { aiResponse, formattedAnalysis } = await analyzeWithAI({
          allowedModels: selectedAiReport?.AllowedModels,
          executionControl: {
            onProgress: updateAnalysisProgress,
            signal: abortController.signal,
            setCancelHandler: (handler) => {
              if (requestIdRef.current === requestId) {
                cancelRunRef.current = handler;
              }
            },
          },
          filters,
          reportResponse,
          selectedReport,
          texts,
          toast,
          t,
          user,
        });

        if (requestIdRef.current !== requestId) return null;

        const aiServiceFailure = !aiResponse.success || isAiServiceFailure(aiResponse);
        const fullReport = {
          ...reportResponse,
          userInput: {
            level: selectedLevel,
            report: selectedReport,
            filters,
            periodLabel:
              filters.periodFrom && filters.periodTo
                ? `${getPeriodLabel(filters.periodFrom)} → ${getPeriodLabel(filters.periodTo)}`
                : getPeriodLabel(filters.period),
            reportName: getAdvancedReportInfo(selectedReport).name,
            hasExcelData: Boolean(filters.uploadedData),
            excelRows: filters.uploadedData?.length || 0,
          },
          aiAnalysis: formattedAnalysis,
          hasAI: aiResponse.success,
          isAIFallback: false,
          aiErrorCode: aiResponse.errorCode || aiResponse.error?.code || "",
          aiErrorStatus: aiResponse.errorStatus || aiResponse.error?.status || "",
          aiError: aiResponse.errorMessage || "",
          userFacingAiError: aiResponse.errorMessage || "",
          aiServiceFailure,
          rawAIResponse: aiResponse,
          timestamp: new Date().toISOString(),
          language: texts?.en,
          allowedModels: selectedAiReport?.AllowedModels,
        };

        setReportApiResponse(fullReport);
        setReportData(reportResponse.data);
        setAiAnalysis(formattedAnalysis);
        window.clearTimeout(progressDismissTimerRef.current);
        setAnalysisProgress(
          aiResponse.success
            ? createCompletedProgress(getText)
            : createErrorProgress(
                aiResponse.errorMessage ||
                  getLocalizedText(
                    t,
                    language,
                    "reports.aiInvalidResponse",
                    "لم يلتزم الذكاء الاصطناعي بعقد JSON المطلوب.",
                    "The AI response did not match the required JSON contract."
                  ),
                getText
              )
        );
        if (aiResponse.success) {
          progressDismissTimerRef.current = window.setTimeout(() => {
            setAnalysisProgress((current) =>
              current?.status === "completed" ? { ...current, active: false } : current
            );
          }, COMPLETED_PROGRESS_HIDE_MS);
        }

        toast({
          title: aiResponse.success
            ? getLocalizedText(t, language, "reports.smartReportReady", "التقرير الذكي جاهز", "Smart report is ready")
            : getLocalizedText(t, language, "reports.aiAnalysisFailed", "فشل التحليل الذكي", "AI analysis failed"),
          description: aiResponse.success
            ? getLocalizedText(t, language, "reports.aiAnalysisComplete", "اكتمل التحليل باستخدام الذكاء الاصطناعي.", "AI analysis completed successfully.")
            : aiResponse.errorMessage ||
              getLocalizedText(
                t,
                language,
                "reports.aiInvalidResponse",
                "لم يلتزم الذكاء الاصطناعي بعقد JSON المطلوب.",
                "The AI response did not match the required JSON contract."
              ),
          status: aiResponse.success ? "success" : "error",
          duration: 4000,
          isClosable: true,
        });

        window.setTimeout(() => {
          reportRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 500);

        return { reportResponse: fullReport, reportData: reportResponse.data, aiAnalysis: formattedAnalysis };
      } catch (error) {
        if (isCancelledError(error) || requestIdRef.current !== requestId) {
          setError(null);
          return null;
        }

        setError(error.message);
        setAnalysisProgress(createErrorProgress(error.message, getText));
        toast({
          title: getLocalizedText(t, language, "reports.analysisError", "خطأ في التحليل", "Analysis error"),
          description:
            error.message ||
            getLocalizedText(t, language, "reports.unknownError", "حدث خطأ غير معروف", "An unknown error occurred"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return null;
      } finally {
        if (requestIdRef.current === requestId) {
          abortControllerRef.current = null;
          cancelRunRef.current = null;
          setIsLoading(false);
          setIsAnalyzing(false);
        }
      }
    },
    [filters, getAdvancedReportInfo, getPeriodLabel, getText, language, resetResults, selectedLevel, selectedReport, t, texts, toast, updateAnalysisProgress, user]
  );

  return {
    isLoading,
    setIsLoading,
    isAnalyzing,
    reportData,
    setReportData,
    reportApiResponse,
    setReportApiResponse,
    aiAnalysis,
    setAiAnalysis,
    usageLimitReached,
    analysisProgress,
    resetResults,
    cancelAnalysis,
    runReport,
  };
};
